import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { loadNowPlaying, type NowPlaying } from "../lib/nowPlaying";
import type { Stream } from "../lib/types";
import { PosterImage } from "./PosterImage";

/**
 * Hub "Live now" card — channel logo with whatever is airing on it.
 *
 * Distinct from `StreamCard` because the programme line is the whole point of
 * the rail; a plain channel tile would just duplicate Favourites. Fetches on
 * entering the viewport and shares the tab-wide cache with the Live TV list,
 * so opening both screens does not double the upstream requests.
 */
export function LiveNowCard({ stream, pid }: { stream: Stream; pid: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [epg, setEpg] = useState<NowPlaying>(null);
  const hasEpg = !!stream.epg_channel_id;

  useEffect(() => {
    if (!hasEpg || !ref.current) return;
    const el = ref.current;
    let cancelled = false;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        loadNowPlaying(pid, Number(stream.id)).then((v) => {
          if (!cancelled) setEpg(v);
        });
      },
      { rootMargin: "200px" },
    );
    io.observe(el);
    return () => {
      cancelled = true;
      io.disconnect();
    };
  }, [pid, stream.id, hasEpg]);

  return (
    <div ref={ref}>
      <Link to={`/watch/live/${encodeURIComponent(stream.id)}`} className="block group">
        <div className="relative aspect-video rounded-card overflow-hidden bg-surface border border-line group-hover:border-line-strong transition-colors duration-press ease-bz">
          <PosterImage
            src={stream.logo || undefined}
            alt={stream.name}
            className="w-full h-full"
            imgClass="w-full h-full object-contain p-4"
            orientation="row"
          />
          <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-pill bg-accent-gold text-canvas text-[9px] font-ui font-bold uppercase tracking-[0.06em]">
            <span className="w-1.5 h-1.5 rounded-full bg-canvas animate-live-pulse" />
            Live
          </span>
          {epg && epg.pct > 0 && (
            <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-line">
              <div className="h-full bg-accent-gold" style={{ width: `${epg.pct}%` }} />
            </div>
          )}
        </div>
        <p className="mt-1.5 text-[12px] font-ui font-medium text-text-primary truncate">
          {stream.name}
        </p>
        {/* Height reserved so an arriving programme title never reflows the rail. */}
        <p className="h-[16px] text-[11px] font-ui text-text-secondary truncate">
          {epg ? epg.title : hasEpg ? "" : "No guide data"}
        </p>
      </Link>
    </div>
  );
}
